import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../../shared/components/ui';
import { Activity, CheckCircle, Info, Zap } from 'lucide-react';
import { DataCoverageInfo } from './DataCoverageInfo';
import { BillValidation } from './BillValidation';
import { PatternAnalysis } from './PatternAnalysis';
import { AlignmentToggle } from './AlignmentToggle';
import { AlignmentComparisonTable } from './AlignmentComparisonTable';
import { usePatternMetrics } from '../../../../hooks/usePatternMetrics';
import { useDemandStatsComparison } from '../../../../hooks/useDemandStatsComparison';

interface ConsumptionData {
  data: Array<{ timestamp: string; consumption: number }>;
}

interface ConsumptionStats {
  startDate: Date | null;
  endDate: Date | null;
  coverage: number;
  totalDays: number;
  missingDays: number;
  hourlyPattern: number[];
  hourlyStdDev?: number[];
  weekdayAverage: number;
  weekendAverage: number;
  seasonalAverages: {
    winter: number;
    spring: number;
    summer: number;
    fall: number;
  };
}

interface CompletionMethod {
  id: string;
  label: string;
  description: string;
}

interface ConsumptionDataSectionProps {
  projectId?: string;
  consumption: ConsumptionData | null;
  consumptionStats: ConsumptionStats;
  billValidation: any;
  isAligned: boolean;
  selectedMethod: string;
  appliedMethod: string | null;
  showOriginal: boolean;
  completionMethods: CompletionMethod[];
  onMethodChange: (method: string) => void;
  onComplete: (shiftMonths?: number, duplicateToYear?: boolean, normalizeToYear?: boolean) => void;
  onToggle: () => void;
  onRevert: () => void;
}

export const ConsumptionDataSection: React.FC<ConsumptionDataSectionProps> = ({
  projectId,
  consumption,
  consumptionStats,
  billValidation,
  isAligned,
  selectedMethod,
  appliedMethod,
  showOriginal,
  completionMethods,
  onMethodChange,
  onComplete,
  onToggle,
  onRevert
}) => {
  const { t } = useTranslation();

  // State for shift and duplicate options
  const [shiftMonths, setShiftMonths] = React.useState<number>(0);
  const [duplicateToYear, setDuplicateToYear] = React.useState<boolean>(false);
  const [normalizeToYear, setNormalizeToYear] = React.useState<boolean>(false);

  // Fetch dynamic pattern metrics
  const { data: metricsData, isLoading: metricsLoading } = usePatternMetrics(
    projectId,
    'consumption',
    isAligned
  );

  // Fetch demand stats comparison
  const { data: demandStats, isLoading: demandStatsLoading } = useDemandStatsComparison(
    projectId,
    isAligned
  );

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-swiss-charcoal flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand-primary" />
          {t('dataAlignment.consumption.title')}
        </h4>
        {isAligned && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
      </div>

      {consumption && consumption.data && consumption.data.length > 0 ? (
        <>
          <DataCoverageInfo {...consumptionStats} />
          
          <BillValidation validation={billValidation} />
          
          <PatternAnalysis
            hourlyPattern={consumptionStats.hourlyPattern}
            hourlyStdDev={consumptionStats.hourlyStdDev}
            weekdayAverage={consumptionStats.weekdayAverage}
            weekendAverage={consumptionStats.weekendAverage}
            seasonalAverages={consumptionStats.seasonalAverages}
            unit="kWh"
            color="#3B82F6"
            title="Consumption (kWh)"
          />

          {/* Data Completion Controls - Reorganized for clarity */}
          <div className="border-t pt-4">
            <h5 className="text-sm font-semibold text-swiss-charcoal mb-4">{t('dataAlignment.consumption.completeDataTitle')}</h5>

            <div className="space-y-4">
              {/* Method Selection */}
              <div className="border-b pb-3">
                <label className="text-sm font-medium text-swiss-charcoal block mb-2">
                  {t('dataAlignment.consumption.completionMethod')}:
                </label>
                <select
                  value={selectedMethod}
                  onChange={(e) => onMethodChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  {completionMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-swiss-stone mt-2">
                  {completionMethods.find(m => m.id === selectedMethod)?.description}
                </p>
              </div>

              {/* Show appropriate controls based on method */}
              {selectedMethod === 'none' ? (
                // Transform Only Mode
                <div className="space-y-3">
                  <h6 className="text-sm font-medium text-swiss-charcoal">
                    {t('dataAlignment.consumption.transformData')}
                  </h6>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={normalizeToYear}
                        onChange={(e) => setNormalizeToYear(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-swiss-charcoal">{t('dataAlignment.consumption.normalizeTo2024')}</span>
                    </label>
                    <p className="text-xs text-swiss-stone ml-6">
                      {t('dataAlignment.consumption.normalizeDescription')}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={duplicateToYear}
                        onChange={(e) => setDuplicateToYear(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-swiss-charcoal">{t('dataAlignment.consumption.duplicateMonths')}</span>
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-swiss-charcoal">
                      {t('dataAlignment.consumption.shiftDataBy')}:
                    </label>
                    <select
                      value={shiftMonths}
                      onChange={(e) => setShiftMonths(parseInt(e.target.value))}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      <option value="0">{t('dataAlignment.consumption.noShift')}</option>
                      <option value="-6">{t('dataAlignment.consumption.shiftBackward', { count: 6 })}</option>
                      <option value="-5">{t('dataAlignment.consumption.shiftBackward', { count: 5 })}</option>
                      <option value="-4">{t('dataAlignment.consumption.shiftBackward', { count: 4 })}</option>
                      <option value="-3">{t('dataAlignment.consumption.shiftBackward', { count: 3 })}</option>
                      <option value="-2">{t('dataAlignment.consumption.shiftBackward', { count: 2 })}</option>
                      <option value="-1">{t('dataAlignment.consumption.shiftBackward', { count: 1 })}</option>
                      <option value="1">{t('dataAlignment.consumption.shiftForward', { count: 1 })}</option>
                      <option value="2">{t('dataAlignment.consumption.shiftForward', { count: 2 })}</option>
                      <option value="3">{t('dataAlignment.consumption.shiftForward', { count: 3 })}</option>
                      <option value="4">{t('dataAlignment.consumption.shiftForward', { count: 4 })}</option>
                      <option value="5">{t('dataAlignment.consumption.shiftForward', { count: 5 })}</option>
                      <option value="6">{t('dataAlignment.consumption.shiftForward', { count: 6 })}</option>
                    </select>
                  </div>

                  <p className="text-xs text-swiss-stone">
                    {normalizeToYear && !duplicateToYear && shiftMonths === 0 &&
                      "✓ Will normalize all timestamps to 2024."}
                    {normalizeToYear && duplicateToYear && shiftMonths === 0 &&
                      "✓ Will normalize to 2024, then duplicate to fill year."}
                    {normalizeToYear && shiftMonths !== 0 &&
                      `✓ Will normalize to 2024, shift by ${Math.abs(shiftMonths)} month${Math.abs(shiftMonths) > 1 ? 's' : ''} ${shiftMonths > 0 ? 'forward' : 'backward'}${duplicateToYear ? ', then duplicate to fill year' : ''}.`}
                    {!normalizeToYear && duplicateToYear && shiftMonths === 0 &&
                      "✓ Will duplicate available months cyclically to create a full year dataset."}
                    {!normalizeToYear && !duplicateToYear && shiftMonths !== 0 &&
                      `✓ Will shift all timestamps by ${Math.abs(shiftMonths)} month${Math.abs(shiftMonths) > 1 ? 's' : ''} ${shiftMonths > 0 ? 'forward' : 'backward'}.`}
                    {!normalizeToYear && duplicateToYear && shiftMonths !== 0 &&
                      `✓ Will shift data by ${Math.abs(shiftMonths)} month${Math.abs(shiftMonths) > 1 ? 's' : ''} ${shiftMonths > 0 ? 'forward' : 'backward'}, then duplicate to fill year.`}
                    {!normalizeToYear && !duplicateToYear && shiftMonths === 0 &&
                      "⚠️ No transformation selected."}
                  </p>

                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => onComplete(shiftMonths, duplicateToYear, normalizeToYear)}
                      disabled={!normalizeToYear && !duplicateToYear && shiftMonths === 0}
                      className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded hover:bg-brand-secondary transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {t('dataAlignment.consumption.applyTransformation')}
                    </button>
                  </div>
                </div>
              ) : (
                // Pattern Completion Mode
                <div className="space-y-3">
                  <div className="border-t pt-3">
                    <p className="text-xs text-swiss-stone font-medium mb-2">{t('dataAlignment.consumption.optionalTransform')}</p>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={duplicateToYear}
                          onChange={(e) => setDuplicateToYear(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-xs text-swiss-charcoal">{t('dataAlignment.consumption.duplicateMonthsFirst')}</span>
                      </label>

                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-swiss-charcoal">
                          {t('dataAlignment.consumption.shiftFirst')}:
                        </label>
                        <select
                          value={shiftMonths}
                          onChange={(e) => setShiftMonths(parseInt(e.target.value))}
                          className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                        >
                          <option value="0">{t('dataAlignment.consumption.noShift')}</option>
                          <option value="-3">-3 {t('dataAlignment.consumption.months')}</option>
                          <option value="-2">-2 {t('dataAlignment.consumption.months')}</option>
                          <option value="-1">-1 {t('dataAlignment.consumption.month')}</option>
                          <option value="1">+1 {t('dataAlignment.consumption.month')}</option>
                          <option value="2">+2 {t('dataAlignment.consumption.months')}</option>
                          <option value="3">+3 {t('dataAlignment.consumption.months')}</option>
                        </select>
                      </div>
                    </div>

                    {(duplicateToYear || shiftMonths !== 0) && (
                      <p className="text-xs text-swiss-stone mt-2">
                        → Will {shiftMonths !== 0 ? `shift by ${Math.abs(shiftMonths)} month(s) ${shiftMonths > 0 ? 'forward' : 'backward'}` : ''}{duplicateToYear && shiftMonths !== 0 ? ', then ' : ''}{duplicateToYear ? 'duplicate' : ''}, then complete with pattern
                      </p>
                    )}
                  </div>

                  {isAligned && (
                    <div className="border-t pt-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-swiss-charcoal">{t('dataAlignment.consumption.patternMetrics')}</span>
                        <div className="relative group">
                          <Info className="w-4 h-4 text-swiss-stone cursor-help" />
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-96 max-h-96 overflow-y-auto p-4 bg-gray-900 text-white text-xs rounded shadow-lg z-50">
                            <div className="mb-3 text-gray-300">{t('dataAlignment.consumption.patternMetricsTooltip')}</div>

                            {metricsLoading ? (
                              <div className="text-gray-400">{t('dataAlignment.consumption.loadingMetrics')}</div>
                            ) : !metricsData || !metricsData.success || !metricsData.metrics?.summary ? (
                              <div className="text-gray-400">
                                {t('dataAlignment.consumption.metricsUnavailable')}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {/* Summary */}
                                <p className={`font-semibold ${
                                  metricsData.metrics.summary.quality === 'excellent' ? 'text-green-400' :
                                  metricsData.metrics.summary.quality === 'good' ? 'text-blue-400' :
                                  metricsData.metrics.summary.quality === 'fair' ? 'text-yellow-400' :
                                  'text-red-400'
                                }`}>
                                  {metricsData.metrics.summary.quality_label}: {metricsData.metrics.summary.within_target}/{metricsData.metrics.summary.total_metrics} metrics within target ({metricsData.metrics.summary.pct_within_target}%)
                                </p>

                                {/* Metrics within target */}
                                {metricsData.metrics.summary.all_metrics.filter(m => m.within_target).length > 0 && (
                                  <>
                                    <p className="font-medium text-green-400 mt-2">{t('dataAlignment.consumption.withinTarget')}</p>
                                    <ul className="ml-4 list-disc space-y-1 text-gray-200">
                                      {metricsData.metrics.summary.all_metrics
                                        .filter(m => m.within_target)
                                        .slice(0, 5)
                                        .map(m => (
                                          <li key={m.name}>{m.name}: {m.value.toFixed(2)}% ✅</li>
                                        ))}
                                    </ul>
                                  </>
                                )}

                                {/* Metrics needing improvement */}
                                {metricsData.metrics.summary.all_metrics.filter(m => !m.within_target).length > 0 && (
                                  <>
                                    <p className="font-medium text-yellow-400 mt-2">{t('dataAlignment.consumption.needsImprovement')}</p>
                                    <ul className="ml-4 list-disc space-y-1 text-gray-200">
                                      {metricsData.metrics.summary.all_metrics
                                        .filter(m => !m.within_target)
                                        .slice(0, 5)
                                        .map(m => (
                                          <li key={m.name}>{m.name}: {m.value.toFixed(2)}% ({t('dataAlignment.consumption.targetLessThan')})</li>
                                        ))}
                                    </ul>
                                  </>
                                )}

                                <p className="text-gray-400 mt-3 italic border-t border-gray-700 pt-2">{t('dataAlignment.consumption.metricsNote')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-2 border-t">
                    <button
                      onClick={() => onComplete(shiftMonths, duplicateToYear)}
                      className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded hover:bg-brand-secondary transition-colors"
                    >
                      {isAligned && appliedMethod !== selectedMethod ?
                        t('dataAlignment.consumption.reCompleteWith', { method: completionMethods.find(m => m.id === selectedMethod)?.label }) :
                        t('dataAlignment.consumption.completeToFullYear')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Alignment Toggle - Moved above Alignment Validation */}
          <AlignmentToggle
            isAligned={isAligned}
            showOriginal={showOriginal}
            appliedMethod={appliedMethod}
            selectedMethod={selectedMethod}
            completionMethods={completionMethods}
            onToggle={onToggle}
            onRevert={onRevert}
          />

          {/* Alignment Comparison Table */}
          {isAligned && (
            <div className="border-t pt-4">
              <h5 className="text-sm font-semibold text-swiss-charcoal mb-3">{t('dataAlignment.consumption.alignmentValidation')}</h5>
              <AlignmentComparisonTable
                projectId={projectId}
                dataType="consumption"
                isAligned={isAligned}
                title="Consumption Alignment Comparison"
              />
            </div>
          )}

          {/* Demand Data Stats Comparison - Below Alignment Validation */}
          {isAligned && demandStats && !demandStatsLoading && (
            <div className="p-3 bg-white rounded border border-swiss-mist">
              <h5 className="text-sm font-semibold text-swiss-charcoal mb-3">
                {t('dataAlignment.consumption.demandComparison')}
              </h5>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-swiss-stone mb-1.5">{t('dataAlignment.consumption.maxKwAllHours')}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-swiss-charcoal">
                      {t('dataAlignment.consumption.original')}: {demandStats.original.max_kw.toLocaleString()} kW
                    </span>
                    <span className="text-swiss-stone">→</span>
                    <span className="font-medium text-brand-primary">
                      {t('dataAlignment.consumption.aligned')}: {demandStats.aligned.max_kw.toLocaleString()} kW
                    </span>
                  </div>
                  {Math.abs(demandStats.original.max_kw - demandStats.aligned.max_kw) < 1 && (
                    <p className="text-xs text-green-600 mt-1">✓ {t('dataAlignment.consumption.excellentMatch')}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-swiss-stone mb-1.5">{t('dataAlignment.consumption.maxKwPeakHours')}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-swiss-charcoal">
                      {t('dataAlignment.consumption.original')}: {demandStats.original.max_kw_peak_hours.toLocaleString()} kW
                    </span>
                    <span className="text-swiss-stone">→</span>
                    <span className="font-medium text-brand-primary">
                      {t('dataAlignment.consumption.aligned')}: {demandStats.aligned.max_kw_peak_hours.toLocaleString()} kW
                    </span>
                  </div>
                  {Math.abs(demandStats.original.max_kw_peak_hours - demandStats.aligned.max_kw_peak_hours) < 1 && (
                    <p className="text-xs text-green-600 mt-1">✓ {t('dataAlignment.consumption.excellentMatch')}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-swiss-stone">
          <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>{t('dataAlignment.consumption.noDataUploaded')}</p>
        </div>
      )}
    </Card>
  );
};